import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Calculator, ScanBarcode, Percent, Printer } from "lucide-react";
import { toast } from "sonner";

const DEFAULTS = {
  default_margin_percent: 30,
  default_fake_discount_percent: 10,
  whatsapp_cost_per_message: 0.85,
  post_purchase_daily_cap: 0,
  pos_roundoff_enabled: true,
  pos_roundoff_threshold: 5,
  pos_roundoff_cap: 5,
  purchase_roundoff_enabled: true,
  purchase_roundoff_step: 0.5,
  gst_mode: "excluded",
  hide_gst_on_customer_bill: false,
  barcode_prefix: "TSS",
  barcode_start: 11885,
  barcode_digits: 5,
  bill_show_phone: true,
  bill_show_description: true,
  bill_brand_name: "",
  bill_contact_phone: "",
};

export default function OrgSettings() {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const set = (k, v) => setSettings((prev) => ({ ...prev, [k]: v }));

  const load = async () => {
    try {
      const { data } = await api.get("/organisation/settings");
      setSettings({ ...DEFAULTS, ...data });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put("/organisation/settings", settings);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="org-settings-page">
      {/* Pricing Defaults */}
      <Card className="p-6 rounded-sm border-slate-200">
        <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><Percent className="w-4 h-4" />Pricing Defaults</h2>
        <p className="text-xs text-slate-500 mb-4">Used when creating new products — users can override per product.</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Default Margin %</Label>
            <Input type="number" min="0" step="0.01" value={settings.default_margin_percent}
              onChange={(e) => set("default_margin_percent", parseFloat(e.target.value) || 0)}
              className="rounded-none mt-1" data-testid="settings-default-margin" />
          </div>
          <div>
            <Label>Default Fake Discount %</Label>
            <Input type="number" min="0" max="99" step="0.01" value={settings.default_fake_discount_percent}
              onChange={(e) => set("default_fake_discount_percent", parseFloat(e.target.value) || 0)}
              className="rounded-none mt-1" data-testid="settings-default-fake-discount" />
          </div>
        </div>
      </Card>

      {/* POS Round-off */}
      <Card className="p-6 rounded-sm border-slate-200">
        <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><Calculator className="w-4 h-4" />POS Round-off</h2>
        <p className="text-xs text-slate-500 mb-4">Auto-rounds the POS grand total to the nearest ₹10. Editable in the POS terminal.</p>
        <div className="flex items-center justify-between mb-4 bg-slate-50 border border-slate-200 p-3">
          <div className="text-sm">Enable round-off on POS bills</div>
          <Switch checked={!!settings.pos_roundoff_enabled} onCheckedChange={(v) => set("pos_roundoff_enabled", v)} data-testid="settings-pos-roundoff-enabled" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Threshold (last digit ≥ N rounds up)</Label>
            <Input type="number" min="1" max="9" step="1" value={settings.pos_roundoff_threshold}
              onChange={(e) => set("pos_roundoff_threshold", parseInt(e.target.value, 10) || 5)}
              disabled={!settings.pos_roundoff_enabled}
              className="rounded-none mt-1" data-testid="settings-pos-threshold" />
            <p className="text-[11px] text-slate-500 mt-1">Default 5: 1446→1450 (digit 6 ≥ 5), 1443→1440 (digit 3 &lt; 5).</p>
          </div>
          <div>
            <Label>Maximum adjustment (₹)</Label>
            <Input type="number" min="0" step="0.5" value={settings.pos_roundoff_cap}
              onChange={(e) => set("pos_roundoff_cap", parseFloat(e.target.value) || 0)}
              disabled={!settings.pos_roundoff_enabled}
              className="rounded-none mt-1" data-testid="settings-pos-cap" />
            <p className="text-[11px] text-slate-500 mt-1">If adjustment would exceed this, no round-off is applied.</p>
          </div>
        </div>
      </Card>

      {/* Purchase Bill Round-off */}
      <Card className="p-6 rounded-sm border-slate-200">
        <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><Calculator className="w-4 h-4" />Purchase Bill Round-off</h2>
        <p className="text-xs text-slate-500 mb-4">Rounds purchase bill grand totals to the nearest ₹{settings.purchase_roundoff_step || 0.5}.</p>
        <div className="flex items-center justify-between mb-4 bg-slate-50 border border-slate-200 p-3">
          <div className="text-sm">Enable round-off on purchase bills</div>
          <Switch checked={!!settings.purchase_roundoff_enabled} onCheckedChange={(v) => set("purchase_roundoff_enabled", v)} data-testid="settings-purchase-roundoff-enabled" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Round-off step (₹)</Label>
            <Input type="number" min="0.01" step="0.01" value={settings.purchase_roundoff_step}
              onChange={(e) => set("purchase_roundoff_step", parseFloat(e.target.value) || 0.5)}
              disabled={!settings.purchase_roundoff_enabled}
              className="rounded-none mt-1" data-testid="settings-purchase-step" />
            <p className="text-[11px] text-slate-500 mt-1">Default 0.5 — rounds to nearest 50 paise.</p>
          </div>
        </div>
      </Card>

      {/* GST Mode */}
      <Card className="p-6 rounded-sm border-slate-200">
        <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><Percent className="w-4 h-4" />GST Handling</h2>
        <p className="text-xs text-slate-500 mb-4">How GST is applied at the POS and whether it's shown on the customer bill.</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>GST Mode</Label>
            <Select value={settings.gst_mode} onValueChange={(v) => set("gst_mode", v)}>
              <SelectTrigger className="rounded-none mt-1" data-testid="settings-gst-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="excluded">Exclusive — GST added on top of selling price</SelectItem>
                <SelectItem value="included">Inclusive — selling price already includes GST</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-500 mt-1">"Inclusive" treats your MRP / selling price as final-with-GST; the tax portion is back-calculated.</p>
          </div>
          <div className="flex items-center justify-between bg-slate-50 border border-slate-200 p-3">
            <div className="text-sm pr-3">
              Hide GST rows on customer bill
              <div className="text-[11px] text-slate-500 mt-1">When ON: invoice still records CGST/SGST internally (for filings) but does not print GST rows on the printable bill.</div>
            </div>
            <Switch checked={!!settings.hide_gst_on_customer_bill} onCheckedChange={(v) => set("hide_gst_on_customer_bill", v)} data-testid="settings-hide-gst" />
          </div>
        </div>
      </Card>

      {/* Barcode Series */}
      <Card className="p-6 rounded-sm border-slate-200">
        <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><ScanBarcode className="w-4 h-4" />Barcode Series</h2>
        <p className="text-xs text-slate-500 mb-4">Auto-generated barcodes for new products (e.g. TSS11885 → TSS11886 → …). Changing the start point skips over any existing numbers in the database.</p>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>Prefix</Label>
            <Input value={settings.barcode_prefix}
              onChange={(e) => set("barcode_prefix", e.target.value.toUpperCase().slice(0, 6))}
              className="rounded-none mt-1 font-mono" data-testid="settings-barcode-prefix" />
          </div>
          <div>
            <Label>Start From</Label>
            <Input type="number" min="0" step="1" value={settings.barcode_start}
              onChange={(e) => set("barcode_start", parseInt(e.target.value, 10) || 0)}
              className="rounded-none mt-1" data-testid="settings-barcode-start" />
          </div>
          <div>
            <Label>Digits (zero-padded)</Label>
            <Input type="number" min="3" max="10" step="1" value={settings.barcode_digits}
              onChange={(e) => set("barcode_digits", parseInt(e.target.value, 10) || 5)}
              className="rounded-none mt-1" data-testid="settings-barcode-digits" />
          </div>
        </div>
        <div className="mt-3 text-xs text-slate-600 bg-slate-50 border border-slate-200 p-2">
          Next barcode preview: <span className="font-mono font-bold">{`${settings.barcode_prefix}${String(settings.barcode_start).padStart(settings.barcode_digits, "0")}`}</span>
        </div>
      </Card>

      {/* Bill Display */}
      <Card className="p-6 rounded-sm border-slate-200">
        <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><Printer className="w-4 h-4" />Customer Bill Display</h2>
        <p className="text-xs text-slate-500 mb-4">Optional header customisations for the printed invoice / WhatsApp bill.</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Brand Name (overrides organisation name)</Label>
            <Input value={settings.bill_brand_name}
              onChange={(e) => set("bill_brand_name", e.target.value)}
              className="rounded-none mt-1" placeholder="e.g. Eaassy Boutique" data-testid="settings-bill-brand" />
          </div>
          <div>
            <Label>Contact Phone on Bill</Label>
            <Input value={settings.bill_contact_phone}
              onChange={(e) => set("bill_contact_phone", e.target.value)}
              className="rounded-none mt-1" placeholder="+91 98765 43210" data-testid="settings-bill-phone" />
          </div>
          <div className="flex items-center justify-between bg-slate-50 border border-slate-200 p-3">
            <div className="text-sm">Print phone number on bill</div>
            <Switch checked={!!settings.bill_show_phone} onCheckedChange={(v) => set("bill_show_phone", v)} data-testid="settings-bill-show-phone" />
          </div>
          <div className="flex items-center justify-between bg-slate-50 border border-slate-200 p-3">
            <div className="text-sm">Show product description column</div>
            <Switch checked={!!settings.bill_show_description} onCheckedChange={(v) => set("bill_show_description", v)} data-testid="settings-bill-show-desc" />
          </div>
        </div>
      </Card>

      {/* WhatsApp Cost Controls */}
      <Card className="p-6 rounded-sm border-slate-200">
        <h2 className="text-lg font-semibold mb-1">WhatsApp Cost Controls</h2>
        <p className="text-xs text-slate-500 mb-4">Per-message rate is used for all "Nearby Estimate" calculations in approval queue & campaign previews.</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Per-message cost (₹)</Label>
            <Input type="number" step="0.01" min="0" value={settings.whatsapp_cost_per_message}
              onChange={(e) => set("whatsapp_cost_per_message", parseFloat(e.target.value) || 0)}
              className="rounded-none mt-1" data-testid="settings-wa-cost" />
          </div>
          <div>
            <Label>Post-purchase daily auto-send cap (₹)</Label>
            <Input type="number" step="1" min="0" value={settings.post_purchase_daily_cap}
              onChange={(e) => set("post_purchase_daily_cap", parseFloat(e.target.value) || 0)}
              className="rounded-none mt-1" data-testid="settings-wa-cap" />
            <p className="text-[11px] text-slate-500 mt-1">Below this, post-purchase thank-you sends auto. Above, queues for approval.</p>
          </div>
        </div>
      </Card>

      <Button onClick={save} className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="settings-save">
        <Save className="w-4 h-4 mr-2" />Save Settings
      </Button>
    </div>
  );
}
